import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ArrowLeft, Briefcase, Calendar, Trash2 } from 'lucide-react';
import { getActivityTypeColor } from '../utils/activityColor';

const DATE_SHORTCUTS = (() => {
  const now = new Date();
  const y = now.getFullYear();
  return [
    { id: 'all', label: 'All time', days: null },
    { id: 'today', label: 'Today', days: 0 },
    { id: 'week', label: 'Last week', days: 7 },
    { id: '3w', label: 'Last 3 weeks', days: 21 },
    { id: '6m', label: 'Last 6 months', days: 183 },
    { id: 'year', label: 'Last year', days: 365 },
    // Calendar-year shortcuts — rolling window of current + two prior
    // years, dynamically computed so the labels stay correct as the
    // year ticks over. Distinct from "Last year" (which is 365 days
    // from today, not Jan 1 → Dec 31).
    { id: `cy-${y}`, label: String(y), yearStart: y },
    { id: `cy-${y - 1}`, label: String(y - 1), yearStart: y - 1 },
    { id: `cy-${y - 2}`, label: String(y - 2), yearStart: y - 2 },
  ];
})();

const ActivityFilterPanel = ({
  activities = [],
  hiddenIds = [],
  onToggle,
  onToggleType,
  commutes,
  onCommutesChange,
  allDates,
  onAllDatesChange,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  activitySettings,
  onDeleteActivity,
  dateRightSlot,
  // When true, shrink the activity-list scroll area so only ~2 rows
  // of the type-chip row are visible at once. Used by the map's
  // right-side panel where vertical space is at a premium.
  compact = false,
  // Toggle for ~2s by the parent's Tipps "Show me" handlers — adds
  // the animate-tip-flash highlight to the date chip / the type-chip
  // container so the user can see which control the tip is pointing at.
  flashDate = false,
  flashTypes = false,
}) => {
  const [expandedType, setExpandedType] = useState(null);
  const [activeShortcut, setActiveShortcut] = useState(allDates ? 'all' : 'custom');
  // Date filter trigger opens a popup with the predefined shortcuts.
  // Picking a non-custom one closes the popup; picking "Custom" keeps
  // the popup open but swaps its body for the From/To date pickers.
  const [datePopupOpen, setDatePopupOpen] = useState(false);
  // Which view the popup currently shows: 'shortcuts' (the preset
  // list) or 'custom' (the from/to date pickers). This is decoupled
  // from `activeShortcut` so a user who's already on "custom" can
  // re-open the popup back at the shortcuts list and switch to a
  // preset without first having to flip back manually.
  const [dateView, setDateView] = useState('shortcuts');
  // The popup is portaled to <body> via createPortal so it isn't clipped
  // by the activity panel's overflow-hidden wrapper. We position it with
  // fixed coords derived from the trigger button's bounding rect, and
  // recompute on scroll / resize so it follows the chip.
  const dateRef = useRef(null);
  const popupRef = useRef(null);
  const [popupPos, setPopupPos] = useState(null);

  useEffect(() => {
    if (!datePopupOpen) return undefined;
    const update = () => {
      const trigger = dateRef.current?.querySelector('button');
      if (!trigger) return;
      const r = trigger.getBoundingClientRect();
      setPopupPos({ top: r.bottom + 6, left: r.left });
    };
    update();
    const onScroll = () => update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [datePopupOpen]);

  useEffect(() => {
    if (!datePopupOpen) return undefined;
    const handler = (e) => {
      // Keep the popup open if the click landed inside the trigger, the
      // popup itself, or a portaled DatePicker calendar.
      if (dateRef.current && dateRef.current.contains(e.target)) return;
      if (popupRef.current && popupRef.current.contains(e.target)) return;
      const dpRoot = document.getElementById('datepicker-portal-root');
      if (dpRoot && dpRoot.contains(e.target)) return;
      setDatePopupOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [datePopupOpen]);

  const hiddenSet = useMemo(() => new Set(hiddenIds), [hiddenIds]);

  const groupedSorted = useMemo(() => {
    const groups = activities.reduce((acc, activity) => {
      const type = activity.type || 'Other';
      if (!acc[type]) acc[type] = [];
      acc[type].push(activity);
      return acc;
    }, {});
    return Object.entries(groups).sort((a, b) => b[1].length - a[1].length);
  }, [activities]);

  const typeHiddenState = (typeActivities) => {
    const hiddenCount = typeActivities.filter(a => hiddenSet.has(a.id)).length;
    if (hiddenCount === 0) return 'all';
    if (hiddenCount === typeActivities.length) return 'none';
    return 'some';
  };

  const handleTypeToggleAll = (type, typeActivities) => {
    const state = typeHiddenState(typeActivities);
    onToggleType?.(type, state === 'all');
  };

  const applyShortcut = (shortcut) => {
    setActiveShortcut(shortcut.id);
    if (shortcut.id === 'all') {
      onAllDatesChange?.(true);
      setDatePopupOpen(false);
      return;
    }
    if (shortcut.id === 'custom') {
      onAllDatesChange?.(false);
      // Switch the popup body to the from/to pickers; keep it open.
      setDateView('custom');
      setDatePopupOpen(true);
      return;
    }
    onAllDatesChange?.(false);
    // Calendar-year shortcuts (Jan 1 → Dec 31 of yearStart) take
    // precedence over the `days`-based ones.
    if (shortcut.yearStart != null) {
      onFromDateChange?.(new Date(shortcut.yearStart, 0, 1));
      onToDateChange?.(new Date(shortcut.yearStart, 11, 31));
      setDatePopupOpen(false);
      return;
    }
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - shortcut.days);
    onFromDateChange?.(start);
    onToDateChange?.(end);
    setDatePopupOpen(false);
  };

  const showDateControls = !!onAllDatesChange;

  const currentShortcutLabel = (() => {
    if (activeShortcut === 'custom') return 'Custom';
    const match = DATE_SHORTCUTS.find(s => s.id === activeShortcut);
    return match ? match.label : 'All time';
  })();

  // Human-readable summary of what's currently filtered — prepended to
  // the "X activities in view" line so the user sees the active range
  // at a glance without re-opening the popup.
  const activeRangeLabel = (() => {
    if (allDates || activeShortcut === 'all') return 'All time';
    const fmt = (d) => {
      if (!(d instanceof Date) || isNaN(d.getTime())) return null;
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${d.getFullYear()}-${m}-${day}`;
    };
    const f = fmt(fromDate);
    const t = fmt(toDate);
    if (f && t) return f === t ? f : `${f} → ${t}`;
    return null;
  })();

  const expandedTypeActivities = expandedType
    ? groupedSorted.find(([t]) => t === expandedType)?.[1] || []
    : [];

  return (
    <div className="border-2 rounded-xl overflow-hidden bg-white">
      <div className="p-1 lg:p-3 border-b space-y-2 lg:space-y-3 bg-gray-100">
        {showDateControls && (
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="relative" ref={dateRef}>
              <button
                type="button"
                data-flash={flashDate ? 'on' : undefined}
                onClick={() => {
                  // Always open back to the shortcuts list so the user
                  // can pick a preset without having to back out of
                  // custom mode manually.
                  setDateView('shortcuts');
                  setDatePopupOpen((o) => !o);
                }}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full border transition-colors ${datePopupOpen
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white border-gray-300 hover:bg-gray-100'
                  } ${flashDate ? 'animate-tip-flash' : ''}`}
                title="Change date filter"
              >
                <Calendar size={14} />
                <span className="text-gray-500 mr-1" style={{ color: datePopupOpen ? 'rgba(255,255,255,0.7)' : undefined }}>
                  Date:
                </span>
                <span className="font-medium">{currentShortcutLabel}</span>
              </button>

              {datePopupOpen && popupPos && createPortal(
                <div
                  ref={popupRef}
                  className="fixed z-[2500] w-64 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden"
                  style={{ top: popupPos.top, left: popupPos.left }}
                >
                  {dateView !== 'custom' ? (
                    // Shortcut list view — pick a preset, or tap "Custom range…"
                    // to switch the popup body to the date pickers.
                    <div className="py-1">
                      {DATE_SHORTCUTS.map((s) => {
                        const isActive = activeShortcut === s.id;
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => applyShortcut(s)}
                            className={`w-full text-left px-3 py-2 text-sm transition-colors ${isActive
                              ? 'bg-gray-100 font-semibold text-gray-900'
                              : 'hover:bg-gray-100 text-gray-800'
                              }`}
                          >
                            {s.label}
                          </button>
                        );
                      })}
                      <div className="border-t border-gray-100" />
                      <button
                        type="button"
                        onClick={() => applyShortcut({ id: 'custom' })}
                        className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 font-medium"
                      >
                        Custom range…
                      </button>
                    </div>
                  ) : (
                    // Custom view — From / To pickers. The "Back to
                    // shortcuts" link returns to the preset list inside
                    // the same popup so the user can switch off custom
                    // without closing.
                    <div className="p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-gray-700">Custom range</span>
                        <button
                          type="button"
                          onClick={() => setDateView('shortcuts')}
                          className="text-[11px] text-gray-500 hover:text-gray-800 underline"
                        >
                          Back to shortcuts
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex flex-col text-xs">
                          <label className="mb-1 text-gray-600">From</label>
                          <DatePicker
                            selected={fromDate}
                            onChange={onFromDateChange}
                            maxDate={toDate}
                            className="w-full px-2 py-1.5 border rounded text-sm"
                            dateFormat="yyyy-MM-dd"
                            showYearDropdown
                            showMonthDropdown
                            dropdownMode="select"
                            yearDropdownItemNumber={30}
                            portalId="datepicker-portal-root"
                            popperClassName="!z-[2000]"
                            popperPlacement="bottom-start"
                            popperProps={{ strategy: 'fixed' }}
                          />
                        </div>
                        <div className="flex flex-col text-xs">
                          <label className="mb-1 text-gray-600">To</label>
                          <DatePicker
                            selected={toDate}
                            onChange={onToDateChange}
                            minDate={fromDate}
                            className="w-full px-2 py-1.5 border rounded text-sm"
                            dateFormat="yyyy-MM-dd"
                            showYearDropdown
                            showMonthDropdown
                            dropdownMode="select"
                            yearDropdownItemNumber={30}
                            portalId="datepicker-portal-root"
                            popperClassName="!z-[2000]"
                            popperPlacement="bottom-end"
                            popperProps={{ strategy: 'fixed' }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>,
                document.body
              )}
            </div>

            <div className="flex items-center gap-2 ml-auto">
              {onCommutesChange && (
                <button
                  type="button"
                  onClick={() => onCommutesChange(!commutes)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full border transition-colors ${commutes
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white border-gray-300 hover:bg-gray-100'
                    }`}
                  title={commutes ? 'Commutes included' : 'Commutes excluded'}
                >
                  <Briefcase size={16} />
                  <span>Commutes {commutes ? '✓' : '×'}</span>
                </button>
              )}
              {dateRightSlot}
            </div>
          </div>
        )}
      </div>

      <div className={`p-2 lg:p-3 space-y-1 lg:space-y-3 overflow-y-auto ${compact ? 'max-h-32 lg:max-h-48' : 'max-h-80'}`}>
        {!expandedType && (
          <div className="text-sm text-center font-medium text-gray-700">
            {activeRangeLabel && (
              <span className="text-gray-500 font-normal">{activeRangeLabel}: </span>
            )}
            {activities.length === 0
              ? 'No activities in current view'
              : `${activities.length} activities in view`}
          </div>
        )}

        {activities.length === 0 && (
          <p className="text-xs text-gray-500 text-center">
            Pan or zoom the map to find activities.
          </p>
        )}

        {/* Overview: chip row of types. Hidden while drilling into a type. */}
        {!expandedType && groupedSorted.length > 0 && (
          <div className={`flex flex-wrap gap-1 lg:gap-3 ${flashTypes ? 'animate-tip-flash rounded-xl p-1 -m-1' : ''}`}>
            {groupedSorted.map(([type, typeActivities]) => {
              const state = typeHiddenState(typeActivities);
              const color = getActivityTypeColor(type, activitySettings);
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => setExpandedType(type)}
                  className="flex items-center gap-2 px-3 py-2 rounded-full border border-gray-300 text-sm transition-colors bg-white hover:bg-gray-100"
                >
                  <input
                    type="checkbox"
                    checked={state === 'all'}
                    ref={el => { if (el) el.indeterminate = state === 'some'; }}
                    onChange={(e) => { e.stopPropagation(); }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTypeToggleAll(type, typeActivities);
                    }}
                    className="h-5 w-5 cursor-pointer"
                    style={{ accentColor: color }}
                  />
                  <span className="font-medium">{type}</span>
                  <span className="text-sm text-gray-500">{typeActivities.length}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Drilled-in view: back button + activities for the expanded type. */}
        {expandedType && (() => {
          const expandedColor = getActivityTypeColor(expandedType, activitySettings);
          return (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setExpandedType(null)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-full border border-gray-300 bg-white hover:bg-gray-100"
                >
                  <ArrowLeft size={16} />
                  <span>Back</span>
                </button>
                <span className="font-semibold text-sm">{expandedType}</span>
                <span className="text-xs text-gray-500">
                  ({expandedTypeActivities.length})
                </span>
              </div>

              <div className="grid grid-cols-1 gap-x-3 gap-y-1">
                {expandedTypeActivities.map(activity => (
                  <div
                    key={activity.id}
                    className="flex items-center gap-2 text-sm min-w-0 py-1"
                  >
                    <label className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!hiddenSet.has(activity.id)}
                        onChange={() => onToggle?.(activity.id)}
                        className="h-5 w-5 flex-shrink-0"
                        style={{ accentColor: expandedColor }}
                      />
                      <span className="truncate flex-1" title={activity.name}>
                        {activity.name}
                      </span>
                    </label>
                    {typeof activity.distance === 'number' && (
                      <span className="text-gray-500 flex-shrink-0 text-xs">
                        {(activity.distance / 1000).toFixed(1)} km
                      </span>
                    )}
                    {onDeleteActivity && (
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(`Delete "${activity.name}"?`)) {
                            onDeleteActivity(activity.id);
                          }
                        }}
                        className="text-gray-400 hover:text-red-500 flex-shrink-0"
                        title="Delete activity"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default ActivityFilterPanel;
